var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { Input, Icon, Popover } from '..';
import { keys } from '../../utilities';
import { toTimeFormat, generateOptions, filterOptionsWithRounding, filterOptions, getClosestOption, getOptionByValue, highlightClosestOption, withClosestOption, } from './utils';
import { TimePickerOptionList } from './components';
export const TimePicker = React.forwardRef((props, ref) => {
    const { className, fluid, defaultTimeSignifier = 'AM', hintIncrement = 30, hintWidth = '100', showHint = false, autoRounding = false, disabled, error, minTime = '12:00 AM', maxTime = '23:59 PM', onChange, placeholder = 'HH:MM ' + defaultTimeSignifier, size, value, direction = 'br', open } = props, rest = __rest(props, ["className", "fluid", "defaultTimeSignifier", "hintIncrement", "hintWidth", "showHint", "autoRounding", "disabled", "error", "minTime", "maxTime", "onChange", "placeholder", "size", "value", "direction", "open"]);
    const inputRef = React.useRef(null);
    const [hintOpened, setHintOpened] = React.useState(showHint && open);
    const [hintFocused, setHintFocused] = React.useState(false);
    const [userInput, setUserInput] = React.useState('');
    const [filterStr, setFilterStr] = React.useState('');
    React.useEffect(() => {
        // Set input's text on each non-null value change
        if (value)
            setUserInput(toTimeFormat.hhmmxm(value) || '');
    }, [value]);
    const handleInputFocus = () => {
        if (hintFocused)
            setHintFocused(false);
        if (hintOpened !== showHint)
            setHintOpened(showHint);
        if (userInput !== '')
            setFilterStr('');
    };
    const handleInputBlur = () => {
        // Disable handleInputBlur in case of hint, as focus may go to hint
        if (showHint)
            return;
        // Fake blur, active element is not really changed (alt+tab, etc)
        if (document.activeElement === inputRef.current.inputRef.current)
            return;
        autoComplete();
    };
    // Initial list of options
    const optionsArgs = [minTime, maxTime, hintIncrement];
    const options = useCallback(generateOptions, optionsArgs)(...optionsArgs);
    // Closest to user's input option
    const closestOptionArgs = [
        options,
        userInput,
        defaultTimeSignifier,
        autoRounding,
        minTime,
        maxTime,
    ];
    const closestOption = useCallback(getClosestOption, closestOptionArgs)(...closestOptionArgs);
    // Filtered list of options with extra one to highligh (in case rounding disabled)
    const hintOptions = useMemo(() => {
        if (userInput === '' || filterStr === '') {
            return options;
        }
        let filtered;
        if (autoRounding) {
            filtered = filterOptionsWithRounding(options, filterStr);
        }
        else {
            filtered = filterOptions(options, filterStr);
        }
        // Insert closest option in case it was filtered out
        filtered = withClosestOption(filtered, closestOption);
        filtered = highlightClosestOption(filtered, closestOption);
        return filtered;
    }, [userInput, filterStr, options, autoRounding, closestOption]);
    // Runs on each input blur event
    const autoComplete = () => {
        var _a, _b, _c;
        // User is choosing from hint's options
        if (hintFocused)
            return;
        if (closestOption) {
            // Accept user input
            if (((_b = (_a = closestOption.value) === null || _a === void 0 ? void 0 : _a.getTime) === null || _b === void 0 ? void 0 : _b.call(_a)) !== ((_c = value === null || value === void 0 ? void 0 : value.getTime) === null || _c === void 0 ? void 0 : _c.call(value)))
                onChange(closestOption.value);
            if (userInput !== closestOption.text)
                setUserInput(closestOption.text);
            if (hintOpened)
                setHintOpened(false);
        }
        else if (userInput !== '') {
            // Reject user input
            onChange(null);
            setUserInput('');
            setFilterStr('');
            if (hintOpened)
                setHintOpened(false);
        }
    };
    // Listening for user's typing
    const handleInputChange = (e, data) => {
        setUserInput(data.value.toUpperCase());
        setFilterStr(data.value.toUpperCase());
        if (!hintOpened)
            setHintOpened(showHint);
    };
    const handleClickOutside = () => {
        if (hintOpened)
            setHintOpened(false);
        autoComplete();
    };
    const handleIconClick = () => {
        setHintOpened(!disabled && showHint && !hintOpened);
        inputRef.current.focus();
    };
    const handleKeyDown = (e) => {
        const { key } = e;
        // Return focus back to the input field from hint
        if (key === keys.esc) {
            e.preventDefault();
            setHintOpened(false);
            inputRef.current.focus();
        }
        // Autocomplete on focus lost (on tab or shift+tab)
        if (key === keys.tab)
            autoComplete();
        // Explicit autocomplete (on enter)
        if (key === keys.enter)
            autoComplete();
        // Move focus to the hint
        if (key === keys.down || key === keys.up) {
            if (hintOpened && !hintFocused) {
                e.preventDefault();
                setHintFocused(true);
            }
        }
    };
    const hintFocusValue = () => {
        const focusValue = closestOption
            ? getOptionByValue(hintOptions, closestOption.value)
            : hintOptions[0];
        return hintFocused ? focusValue : null;
    };
    const handleHintOnChange = (value) => {
        inputRef.current.focus(); // Move focus back to the field
        onChange(value);
        setUserInput(toTimeFormat.hhmmxm(value));
        setFilterStr('');
        if (hintOpened)
            setHintOpened(false);
    };
    const TimePickerClasses = classnames(className, 'TimePicker', {
        'TimePicker--disabled': disabled,
        [`TimePicker--${size}`]: size,
    });
    const InputProps = {
        fluid,
        disabled,
        error,
        placeholder,
        size,
        onChange: handleInputChange,
        onFocus: handleInputFocus,
        onBlur: handleInputBlur,
        value: userInput,
    };
    const HintProps = {
        className: classnames('TimePicker__options', {
            'TimePicker__options-focused': hintFocused,
        }),
        focusValue: hintFocusValue(),
        options: hintOptions,
        onChange: handleHintOnChange,
        tabIndex: -1,
        type: 'time',
    };
    const Field = (React.createElement(Input, Object.assign({}, InputProps, { className: "TimePicker__input", ref: inputRef, shortLabel: React.createElement(Icon, { name: "access_time", className: "TimePicker__icon", onClick: handleIconClick }) })));
    // Autocomplete on first render to handle case of value:string, converting it to value:datetime
    React.useEffect(() => {
        autoComplete();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);
    return (React.createElement(Popover, Object.assign({ autoFlipVertically: true, className: classnames('TimePicker__hint', 'a-Select'), direction: direction, onClickOutside: handleClickOutside, onKeyDown: handleKeyDown, open: !disabled &&
            showHint &&
            hintOpened &&
            hintOptions.length !== 0, padding: null, portal: props.portal || true, ref: ref, scrollHeight: props.scrollHeight || '192px', trigger: Field, width: hintWidth, wrapperClassName: TimePickerClasses }, rest),
        React.createElement(TimePickerOptionList, Object.assign({}, HintProps))));
});
TimePicker.displayName = 'TimePicker';
//# sourceMappingURL=TimePicker.js.map